// Named travellers of an itinerary version and who of them takes part in one
// item.
//
// `itinerary_version.travellers_contact_info` is a list of colon-joined
// strings `name:age:phone[:travelerTypeId]`. Every older reader only looks at
// the first three slots, so the optional fourth (the traveller type — Adult,
// Child, …) is invisible to them, and an untyped entry is written back as the
// original three-slot string.
//
// An item's participants are stored as the names that do NOT take part
// (`details.excludedTravellers`), so a traveller added later in Planner
// settings participates everywhere by default. Party members nobody has named
// yet are left out by count per traveller type
// (`details.excludedUnnamedTravellers`).
import { travellerHeads } from './effective-travellers';

export type TravellerContact = {
  name: string;
  age: string;
  phone: string;
  travelerTypeId: string;
};

export const parseTravellerContact = (entry: unknown): TravellerContact => {
  const [name = '', age = '', phone = '', travelerTypeId = ''] = String(
    entry ?? ''
  ).split(':');
  return {
    name: name.trim(),
    age: age.trim(),
    phone: phone.trim(),
    travelerTypeId: travelerTypeId.trim(),
  };
};

export const parseTravellerContacts = (raw: unknown): TravellerContact[] =>
  Array.isArray(raw)
    ? raw
        .filter((entry): entry is string => typeof entry === 'string')
        .map(parseTravellerContact)
    : [];

export const encodeTravellerContact = (contact: TravellerContact): string => {
  const base = `${contact.name}:${contact.age}:${contact.phone}`;
  return contact.travelerTypeId ? `${base}:${contact.travelerTypeId}` : base;
};

/** The excluded names stored on an item's `details` (trimmed, de-duplicated). */
export const readExcludedTravellers = (details: unknown): string[] => {
  if (details == null || typeof details !== 'object') return [];
  const raw = (details as { excludedTravellers?: unknown }).excludedTravellers;
  if (!Array.isArray(raw)) return [];
  const names = raw
    .filter((name): name is string => typeof name === 'string')
    .map((name) => name.trim())
    .filter(Boolean);
  return [...new Set(names)];
};

export const sameNameSet = (a: string[], b: string[]): boolean => {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((name) => right.has(name));
};

export type ParticipantPeople = {
  people: Array<{ travelerTypeId: string; title: string }>;
};

/** Unnamed party members left out of an item, as a count per traveller type. */
export type ExcludedUnnamed = Record<string, number>;

export const readExcludedUnnamed = (details: unknown): ExcludedUnnamed => {
  if (details == null || typeof details !== 'object') return {};
  const raw = (details as { excludedUnnamedTravellers?: unknown })
    .excludedUnnamedTravellers;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const counts: ExcludedUnnamed = {};
  for (const [typeId, count] of Object.entries(raw)) {
    if (typeId && Number.isInteger(count) && (count as number) > 0) {
      counts[typeId] = count as number;
    }
  }
  return counts;
};

export const sameUnnamedCounts = (
  a: ExcludedUnnamed,
  b: ExcludedUnnamed
): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => (a[key] ?? 0) === (b[key] ?? 0));
};

export const hasUnnamedExclusions = (counts: ExcludedUnnamed): boolean =>
  Object.values(counts).some((count) => count > 0);

export type UnnamedSlot = {
  travelerTypeId: string;
  title: string;
  count: number;
};

/**
 * Party members with no named traveller, per type: the `base` heads of a type
 * minus the named travellers given that type. Types appear in party order.
 */
export const unnamedSlots = (
  base: unknown,
  contacts: TravellerContact[]
): UnnamedSlot[] => {
  const named = new Map<string, number>();
  for (const contact of contacts) {
    if (!contact.travelerTypeId) continue;
    named.set(
      contact.travelerTypeId,
      (named.get(contact.travelerTypeId) ?? 0) + 1
    );
  }
  const slots: UnnamedSlot[] = [];
  for (const head of travellerHeads(base)) {
    const typeId = head.travellerTypeId ?? '';
    if (!typeId) continue;
    const slot = slots.find((s) => s.travelerTypeId === typeId);
    if (slot) slot.count += 1;
    else
      slots.push({ travelerTypeId: typeId, title: head.title ?? '', count: 1 });
  }
  return slots
    .map((slot) => ({
      ...slot,
      count: Math.max(0, slot.count - (named.get(slot.travelerTypeId) ?? 0)),
    }))
    .filter((slot) => slot.count > 0);
};

/** `counts` capped to the unnamed members the party actually has. */
export const clampExcludedUnnamed = (
  counts: ExcludedUnnamed,
  base: unknown,
  contacts: TravellerContact[]
): ExcludedUnnamed => {
  const clamped: ExcludedUnnamed = {};
  for (const slot of unnamedSlots(base, contacts)) {
    const count = Math.min(counts[slot.travelerTypeId] ?? 0, slot.count);
    if (count > 0) clamped[slot.travelerTypeId] = count;
  }
  return clamped;
};

/**
 * The item's head-count once `excluded` travellers drop out: one head of each
 * excluded traveller's type is removed from the `base` composition (the
 * travellers the item would carry with everyone taking part), plus
 * `excludedUnnamed` heads per type for members nobody has named. A name that
 * is not in `contacts`, or has no type, removes nobody — see
 * `untypedExcludedTravellers`.
 */
export const participantPeople = (
  base: unknown,
  contacts: TravellerContact[],
  excluded: string[],
  excludedUnnamed: ExcludedUnnamed = {}
): ParticipantPeople => {
  const toRemove = new Map<string, number>(Object.entries(excludedUnnamed));
  const excludedSet = new Set(excluded);
  for (const contact of contacts) {
    if (!excludedSet.has(contact.name) || !contact.travelerTypeId) continue;
    toRemove.set(
      contact.travelerTypeId,
      (toRemove.get(contact.travelerTypeId) ?? 0) + 1
    );
  }
  const people: ParticipantPeople['people'] = [];
  for (const head of travellerHeads(base)) {
    const typeId = head.travellerTypeId ?? '';
    const pending = toRemove.get(typeId) ?? 0;
    if (pending > 0) {
      toRemove.set(typeId, pending - 1);
      continue;
    }
    people.push({ travelerTypeId: typeId, title: head.title ?? '' });
  }
  return { people };
};

/** Excluded names whose traveller has no type, so cannot leave the count. */
export const untypedExcludedTravellers = (
  contacts: TravellerContact[],
  excluded: string[]
): string[] => {
  const excludedSet = new Set(excluded);
  return contacts
    .filter(
      (contact) => excludedSet.has(contact.name) && !contact.travelerTypeId
    )
    .map((contact) => contact.name);
};
